import React, { useEffect, useState } from "react";
import { Checkbox } from "@heroui/react";
import {
  EditableInputComponent,
  EditableSelectComponent,
} from "components/index";
import SearchComponent, {
  SearchMode,
} from "shared/components/searchBar/components/searchComponent";
import { ResourceType } from "shared/types/resource";
import { useResources } from "shared/contexts/resources.context";
import ReactDatePicker from "react-datepicker";

// Type assertion for React 18 compatibility
const DatePicker = ReactDatePicker as any;

import {
  ContactType,
  ContractLegalForm,
  ContractPhase,
  InvoicingFrequency,
  RentalContract,
} from "shared/types/rental-contracts";
import { UnitDTO } from "shared/types/unit";
import { BuildingDTO } from "shared/types/building";
import { YallyCompany } from "shared/types/yally-company";
import { Close, Education, Plus } from "assets/icons";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession, useUser } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import ExtractionReviewBanner from "./contractExtraction/ExtractionReviewBanner";
import { mapExtractedContract } from "./contractExtraction/mapExtractedContract";
import { ExtractedContractDto } from "./contractExtraction/types";
import { analyzeContract, PreviewDocument } from "./contractExtraction/contractExtractionApi";
import ContractWizardMethodStep from "./contractExtraction/ContractWizardMethodStep";
import IndexedRentNotice from "./contractExtraction/IndexedRentNotice";
import { AttributeExistingContractBadge } from "shared/components/rental/components/AttributeExistingContractBadge";
import { getAttributeExistingContract } from "screens/sites/components/buildingAttributeStatus";
import { CounterField, Operation } from "components/counterField/counterField";
import ButtonNew from "components/buttonNew/buttonNew";
import { FloppyDisk, Trash, Warning, ArrowSquareOut } from "@phosphor-icons/react";
import { FileLabel, FileLog } from "shared/types/file";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Rental-related document labels
const RENTAL_RELATED_LABELS = [
  FileLabel.Huurcontract,
  FileLabel.AnnexHuurcontract,
  FileLabel.AddendumHuurcontract,
  // Addendum waarmee een garage/parking aan een lopend woninghuurcontract wordt gehangen.
  FileLabel.GarageAddendumContract,
  FileLabel.Huurhernieuwingsaanvraag,
  FileLabel.Huurwaarborg,
  FileLabel.IngaandePlaatsbeschrijving,
  FileLabel.UitgaandePlaatsbeschrijving,
  FileLabel.IdentiteitskaartHuurder,
  FileLabel.BetalingsbewijsHuur,
  FileLabel.Indexatiebrief,
  FileLabel.BrandverzekeringHuurder,
  FileLabel.CommunicatieHuurder,
  FileLabel.RegistratiebewijsHuurcontract,
  FileLabel.Opzegbrief,
  FileLabel.RegistratiebewijsPlaatsbeschrijving,
  FileLabel.ReglementInwendigeOrde,
  FileLabel.JusticeOfPeaceCommunication,
  FileLabel.JusticeOfPeaceDeed,
  FileLabel.JusticeOfPeaceRequest,
  FileLabel.JusticeOfPeaceSummons,
  FileLabel.BundleJusticeOfPeace,
  FileLabel.BundleLawyerCounterparty,
  FileLabel.CommunicationLawyerOwn,
  FileLabel.CommunicationLawyerCounterparty,
];

interface Product {
  name: string;
  basePrice: number;
  indexedPrice: number;
  templateId: number | null;
  id?: number | null;
  ledger: string | null;
  indexable: boolean;
  odooProductVariantId: number | null;
  odooTemplateName: string | null;
  archived: boolean;
  buildingAttributeId?: number | null;
  vatRate?: number;
}

interface BuildingAttribute {
  id: number;
  name: string;
  description: string;
  template: {
    id: number;
    category: string;
    ledger: string;
    productTemplates: Array<{
      id: number;
      name: string;
      company: {
        id: number;
        name: string;
      };
    }>;
  };
  odooAnalyticAccountName?: string | null;
  /**
   * Contract lines this attribute is (still) part of. The API returns every non-archived attribute,
   * also the ones on a lopend / concept / in-opzeg contract, so the wizard can show that context
   * instead of hiding the attribute (see AttributeExistingContractBadge).
   */
  contractProducts?: Array<{
    id?: number;
    contract?: {
      id?: number;
      phase?: string;
      unit?: { id?: string } | null;
      contractContacts?: Array<{ contactType?: string; resource?: { name?: string } | null }> | null;
    };
  }> | null;
}

interface ModalAddContractProps {
  company: YallyCompany;
  units: UnitDTO[];
  /** Legacy modal flag — defaults to true now that this renders as a page panel. */
  isOpen?: boolean;
  onClose: () => void;
  refreshContracts?: () => void;
  /** Report the contract document to preview (set on analyse/upload, cleared otherwise). */
  onPreviewDocument?: (doc: PreviewDocument | null) => void;
  prefilledContacts?: Array<{
    id: number;
    name: string;
    isOnContract: boolean;
    resourceEntityId?: number;
    wasExisting?: boolean;
    residentData?: {
      email?: string | null;
      phoneNumber?: string | null;
      spokenLanguages?: string[];
      nationality?: string | null;
      dateOfBirth?: string | null;
      placeOfBirth?: string | null;
      relationToApplicant?: string | null;
    } | null;
  }>;
  prefilledUnitId?: string;
  prefilledContractType?: string;
  prefilledNumResidents?: number;
  /**
   * The building this modal operates in. Required for attribute-only contracts (a contract for a
   * standalone building attribute such as a parking) so we know which building/kostenplaats applies.
   */
  building?: BuildingDTO;
  /** Open the modal directly in attribute-only mode (no unit, rent a building attribute on its own). */
  attributeOnlyDefault?: boolean;
  /** Pre-select and auto-add this building attribute when the modal opens (used from the attribute pages). */
  prefilledBuildingAttributeId?: number;
}

const AddContractForm: React.FC<ModalAddContractProps> = ({
  company,
  units,
  isOpen = true,
  onClose,
  refreshContracts,
  onPreviewDocument,
  prefilledContacts,
  prefilledUnitId,
  prefilledContractType,
  prefilledNumResidents,
  building,
  attributeOnlyDefault = false,
  prefilledBuildingAttributeId,
}) => {
  // Attribute-only mode: rent out a standalone building attribute (e.g. a parking) without a unit.
  const [attributeOnly, setAttributeOnly] = useState<boolean>(attributeOnlyDefault);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [productTemplates, setProductTemplates] = useState<Product[]>([]);
  const [buildingAttributes, setBuildingAttributes] = useState<BuildingAttribute[]>([]);
  const [priceInputValues, setPriceInputValues] = useState<{ [key: string]: string }>({});
  const [contractDetails, setContractDetails] = useState<RentalContract>({
    legalForm: null,
    unitId: "",
    phase: ContractPhase.CONCEPT,
    contractContacts: [],
    fileLogIds: [],
    fileLogs: [],
    numResidents: 0,
    missingFileLogLabels: [],
  });
  // Contract contacts state - list of contacts with their types
  const [contractContacts, setContractContacts] = useState<Array<{
    contactType: ContactType | null;
    resourceId: number | null;
    resource: any;
    wasExisting?: boolean;
    residentData?: any;
  }>>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [equalPriceConfirmOpen, setEqualPriceConfirmOpen] = useState(false);
  const [equalPriceConfirmed, setEqualPriceConfirmed] = useState(false);
  const [equalPriceProducts, setEqualPriceProducts] = useState<
    Array<{ name: string; price: number }>
  >([]);
  const [equalPriceStartDateInfo, setEqualPriceStartDateInfo] = useState<{
    startDate: Date | null;
    daysSinceStart: number | null;
    yearsSinceStart: number | null;
    isSuspicious: boolean;
  }>({ startDate: null, daysSinceStart: null, yearsSinceStart: null, isSuspicious: false });
  const [signDate, setSignDate] = useState<Date | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [numResidents, setNumResidents] = useState(1);
  const { session } = useSession();
  const { user } = useUser();
  const [availableFileLogs, setAvailableFileLogs] = useState<FileLog[]>([]);
  const [selectedFileLogIds, setSelectedFileLogIds] = useState<number[]>([]);
  const [isLoadingFileLogs, setIsLoadingFileLogs] = useState(false);
  // Ella contract-extraction review (fields to double-check + non-fatal warnings).
  const [extractionReview, setExtractionReview] = useState<{
    lowConfidenceFields: string[];
    warnings: string[];
  } | null>(null);
  // Wizard: 'method' = choose how to start (upload / analyse / manual), 'form' = the full contract form.
  const [wizardStep, setWizardStep] = useState<"method" | "form">("method");
  // Existing Huurcontract documents for the selected unit (for "analyse existing").
  const huurcontractFileLogs = availableFileLogs.filter((f) =>
    Array.isArray(f.label)
      ? f.label.includes(FileLabel.Huurcontract)
      : f.label === FileLabel.Huurcontract,
  );

  // Current EPC of the selected unit — shown as a copyable field for the Statbel
  // huurcalculator (the energiecorrectie depends on the EPC label).
  const [unitEpc, setUnitEpc] = useState<{ label: string; kWh: string } | null>(null);
  useEffect(() => {
    const unitId = contractDetails.unitId;
    if (attributeOnly || !unitId) {
      setUnitEpc(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth(
          `${API_URL}/units/${encodeURIComponent(unitId)}/current-epc`,
          { method: "GET" },
          session,
        );
        if (res.ok && !cancelled) setUnitEpc(await res.json());
      } catch (error) {
        console.error("Error fetching unit EPC:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contractDetails.unitId, attributeOnly]);

  // When the modal (re)opens, start at the method step — unless we were given
  // prefilled data (e.g. bulk creation from candidates), then go straight to the form.
  useEffect(() => {
    if (!isOpen) return;
    setWizardStep(
      (prefilledContacts && prefilledContacts.length > 0) || prefilledUnitId
        ? "form"
        : "method",
    );
  }, [isOpen]);

  /**
   * Apply Ella's extracted contract data to the form. Prefills products, dates,
   * regime, invoicing, contacts and attaches the analysed document. Fields Ella
   * was unsure about are left blank and surfaced via the review banner.
   */
  const handleExtractedContract = (data: ExtractedContractDto) => {
    const mapped = mapExtractedContract(data);

    if (mapped.products.length > 0) {
      setSelectedProducts(mapped.products as Product[]);
      setPriceInputValues(mapped.priceInputValues);
    }
    if (mapped.contractContacts.length > 0) {
      setContractContacts(mapped.contractContacts);
    }
    if (mapped.signDate) setSignDate(mapped.signDate);
    if (mapped.startDate) setStartDate(mapped.startDate);
    if (mapped.endDate) setEndDate(mapped.endDate);
    if (mapped.numResidents) setNumResidents(mapped.numResidents);

    setContractDetails((prev) => ({
      ...prev,
      ...(mapped.legalForm ? { legalForm: mapped.legalForm } : {}),
      ...(mapped.invoicingFrequency ? { invoicingFrequency: mapped.invoicingFrequency } : {}),
      ...(mapped.propertyTaxes ? { propertyTaxes: mapped.propertyTaxes as any } : {}),
    }));

    // Attach the analysed document to the contract.
    setSelectedFileLogIds((prev) =>
      prev.includes(mapped.fileLogId) ? prev : [...prev, mapped.fileLogId],
    );

    setExtractionReview({
      lowConfidenceFields: data.lowConfidenceFields || [],
      warnings: data.warnings || [],
    });

    // Move from the wizard's method step to the prefilled form for review.
    setWizardStep("form");
  };

  // Analyse an existing (already uploaded) Huurcontract that the user picks.
  const [analyzingFileLogId, setAnalyzingFileLogId] = useState<number | null>(null);
  const handleAnalyzeExistingFileLog = async (fileLogId: number) => {
    if (analyzingFileLogId) return;
    setAnalyzingFileLogId(fileLogId);
    // Show the picked contract in the preview pane immediately.
    const picked = availableFileLogs.find((f) => f.id === fileLogId);
    if (picked) {
      onPreviewDocument?.({
        sharePointFileId: picked.sharePointFileId,
        sharePointFileUrl: picked.sharePointFileUrl,
        name: Array.isArray(picked.label) ? picked.label.join(", ") : picked.label,
      });
    }
    try {
      const data = await analyzeContract(
        fileLogId,
        {
          companyId: company.id,
          unitId: contractDetails.unitId || undefined,
          buildingId: attributeOnly ? building?.id || undefined : undefined,
          userId: user?.id,
        },
        session,
      );
      handleExtractedContract(data);
      toast.success("Ella heeft het huurcontract uitgelezen.");
    } catch (error: any) {
      console.error("Error analyzing existing rental contract:", error);
      toast.error(error?.message || "Het huurcontract kon niet worden uitgelezen.");
    } finally {
      setAnalyzingFileLogId(null);
    }
  };

  useEffect(() => {
    console.log(company);

    const getContractProductTemplates = async () => {
      try {
        const res = await fetchWithAuth(
          `${API_URL}/companies/${company.id}/product-templates`,
          { method: "GET" },
          session,
        );
        const data = await res.json();
        console.log("Product templates");
        console.log(data);
        setProductTemplates(data);
      } catch (error) {
        console.error("Error fetching rental contracts:", error);
      }
    };

    if (company) getContractProductTemplates();
  }, [company]);

  const fetchBuildingAttributes = async (unitId: string) => {
    if (!unitId) {
      setBuildingAttributes([]);
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/building-attributes/unit/${unitId}/available`,
        { method: "GET" },
        session,
      );
      if (response.ok) {
        const data = await response.json();
        setBuildingAttributes(data);
      }
    } catch (error) {
      console.error("Error fetching building attributes:", error);
      setBuildingAttributes([]);
    }
  };

  // Attribute-only mode: fetch all attributes for the building (no unit context). Attributes that are
  // still on a non-closed contract are included; the list shows their current contract + phase.
  const fetchAvailableBuildingAttributesForBuilding = async (buildingId: string) => {
    if (!buildingId) {
      setBuildingAttributes([]);
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/building-attributes/building/${buildingId}/available`,
        { method: "GET" },
        session,
      );
      if (response.ok) {
        const data = await response.json();
        setBuildingAttributes(data);
      } else {
        setBuildingAttributes([]);
      }
    } catch (error) {
      console.error("Error fetching available building attributes for building:", error);
      setBuildingAttributes([]);
    }
  };

  const fetchRentalFileLogs = async (scope: { unitId?: string; buildingId?: string }) => {
    if (!scope.unitId && !scope.buildingId) {
      setAvailableFileLogs([]);
      return;
    }

    setIsLoadingFileLogs(true);
    try {
      const queryParams = new URLSearchParams();
      if (scope.unitId) queryParams.append('unitId', scope.unitId);
      if (scope.buildingId) queryParams.append('buildingId', scope.buildingId);
      queryParams.append('labels', RENTAL_RELATED_LABELS.join(','));
      
      const response = await fetchWithAuth(
        `${API_URL}/file/get-file-logs?${queryParams.toString()}`,
        { method: "GET" },
        session,
      );
      
      if (response.ok) {
        const data = await response.json();
        
        // Filter for files uploaded in the last 4 months
        const fourMonthsAgo = new Date();
        fourMonthsAgo.setMonth(fourMonthsAgo.getMonth() - 4);
        
        const recentFileLogs = data.filter((fileLog: FileLog) => {
          const createdAt = new Date(fileLog.createdAt || '');
          return createdAt >= fourMonthsAgo;
        });
        
        setAvailableFileLogs(recentFileLogs);
      }
    } catch (error) {
      console.error("Error fetching rental file logs:", error);
      setAvailableFileLogs([]);
    } finally {
      setIsLoadingFileLogs(false);
    }
  };

  useEffect(() => {
    if (attributeOnly) return; // building-scoped fetch handled separately below
    if (contractDetails.unitId) {
      fetchBuildingAttributes(contractDetails.unitId);
      fetchRentalFileLogs({ unitId: contractDetails.unitId });
    }
  }, [contractDetails.unitId, attributeOnly]);

  // In attribute-only mode the available attributes + documents come from the building, not a unit.
  useEffect(() => {
    if (attributeOnly && isOpen && building?.id) {
      fetchAvailableBuildingAttributesForBuilding(building.id);
      fetchRentalFileLogs({ buildingId: building.id });
    }
  }, [attributeOnly, isOpen, building?.id]);

  // Sync the toggle to the requested default whenever the modal (re)opens, and apply sensible
  // defaults for a parking-style contract: Parking legal form + monthly invoicing.
  useEffect(() => {
    if (!isOpen) return;
    setAttributeOnly(attributeOnlyDefault);
    if (attributeOnlyDefault) {
      setContractDetails((prev) => ({
        ...prev,
        unitId: "",
        legalForm: prev.legalForm || ContractLegalForm.PARKING,
        invoicingFrequency: prev.invoicingFrequency || InvoicingFrequency.MONTHLY,
      }));
    }
  }, [isOpen, attributeOnlyDefault]);

  // Auto-add a pre-selected building attribute once the available list has loaded.
  useEffect(() => {
    if (!isOpen || !attributeOnly || !prefilledBuildingAttributeId) return;
    if (buildingAttributes.length === 0) return;
    const alreadyAdded = selectedProducts.some(
      (p) => p.buildingAttributeId === prefilledBuildingAttributeId,
    );
    if (alreadyAdded) return;
    const attribute = buildingAttributes.find((a) => a.id === prefilledBuildingAttributeId);
    if (attribute) {
      handleAddBuildingAttribute(attribute);
    }
  }, [isOpen, attributeOnly, prefilledBuildingAttributeId, buildingAttributes]);

  // Handle prefilling when modal opens
  const { resources, refreshResources } = useResources();

  useEffect(() => {
    if (isOpen && (prefilledContacts || prefilledUnitId || prefilledNumResidents)) {
      // Set prefilled contacts - map all contacts with their appropriate type
      if (prefilledContacts && prefilledContacts.length > 0 && contractContacts.length === 0) {
        // ensure resources are loaded so we can translate contact IDs to resource IDs
        if (resources.length === 0) {
          refreshResources();
        }

        const mappedContacts = prefilledContacts.map(contact => {
          // prefixed contact might already contain a resourceEntityId coming from API
          let rawId = contact.resourceEntityId ?? contact.id;

          // try to resolve to a true Resource.id if we only received a contact id
          if (!contact.resourceEntityId && resources.length > 0) {
            const match = resources.find(
              (r) => r.type === ResourceType.CONTACT && r.resourceId === String(contact.id),
            );
            if (match) {
              // Resource.id is numeric, ensure correct type
              rawId = Number(match.id);
            }
          }

          return {
            // isOnContract = true means TENANT, false means INHABITANT (co-inhabitant)
            contactType: contact.isOnContract ? ContactType.TENANT : ContactType.INHABITANT,
            resourceId: rawId,
            resource: { id: rawId, name: contact.name },
            wasExisting: contact.wasExisting ?? false,
            residentData: contact.residentData ?? null,
          };
        });
        // notify if any contacts could not be mapped to a resource
        if (mappedContacts.some(c => c.resourceId == null)) {
          toast.warn("Sommige voorgedefinieerde contacten konden niet automatisch worden gekoppeld. Kies ze handmatig.");
        }
        setContractContacts(mappedContacts);
      }

      // Set prefilled unit
      if (prefilledUnitId) {
        setContractDetails(prev => ({
          ...prev,
          unitId: prefilledUnitId
        }));
      }

      // Set prefilled number of residents - use explicit count, or fall back to contacts length
      if (prefilledNumResidents && prefilledNumResidents > 0) {
        setNumResidents(prefilledNumResidents);
      } else if (prefilledContacts && prefilledContacts.length > 0) {
        setNumResidents(prefilledContacts.length);
      }
    }
  }, [isOpen, prefilledContacts, prefilledUnitId, prefilledNumResidents]);

  // Handle prefilling contract type after productTemplates are loaded
  useEffect(() => {
    if (isOpen && prefilledContractType && productTemplates.length > 0 && !contractDetails.legalForm) {
      // Map the contract type string to the enum value
      const contractTypeMap: { [key: string]: ContractLegalForm } = {
        'RESIDENTIAL_LONG_FLANDERS': ContractLegalForm.RESIDENTIAL_LONG_FLANDERS,
        'RESIDENTIAL_SHORT_FLANDERS': ContractLegalForm.RESIDENTIAL_SHORT_FLANDERS,
        'RETAIL_LONG_FLANDERS': ContractLegalForm.RETAIL_LONG_FLANDERS,
        'RETAIL_SHORT_FLANDERS': ContractLegalForm.RETAIL_SHORT_FLANDERS,
      };

      const legalForm = contractTypeMap[prefilledContractType];
      if (legalForm) {
        // Use handleContractDetailsChange to trigger the product loading logic
        handleContractDetailsChange('legalForm', legalForm);
      }
    }
  }, [isOpen, prefilledContractType, productTemplates, contractDetails.legalForm]);

  // Handle prefilling product prices from unit's advertised rental price and provisions
  useEffect(() => {
    // Only run when we have prefilled data and products are loaded
    if (!isOpen || !prefilledUnitId || selectedProducts.length === 0) return;

    // Find the selected unit
    const selectedUnit = units.find(u => u.id === prefilledUnitId);
    if (!selectedUnit) return;

    const advertisedRent = selectedUnit.advertisedRentalPrice;
    const advertisedProvisions = selectedUnit.advertisedProvisions;

    // Check if we have prices to prefill and if products don't already have prices set
    const hasRentPrice = advertisedRent && advertisedRent > 0;
    const hasProvisionPrice = advertisedProvisions && advertisedProvisions > 0;

    if (!hasRentPrice && !hasProvisionPrice) return;

    // Find indices of the relevant products
    let updatedProducts = [...selectedProducts];
    let updatedPriceInputs = { ...priceInputValues };
    let hasUpdates = false;

    selectedProducts.forEach((product, index) => {
      const productNameLower = product.name.toLowerCase();

      // Check for "Woninghuur - Huur" product
      if (hasRentPrice && (
        productNameLower.includes("woninghuur - huur") ||
        (productNameLower.includes("- huur") && !productNameLower.includes("provisie"))
      )) {
        // Only update if price is still 0 (not already set)
        if (product.basePrice === 0 && product.indexedPrice === 0) {
          updatedProducts[index] = {
            ...product,
            basePrice: advertisedRent,
            indexedPrice: advertisedRent
          };
          updatedPriceInputs[`${index}-basePrice`] = advertisedRent.toString().replace('.', ',');
          updatedPriceInputs[`${index}-indexedPrice`] = advertisedRent.toString().replace('.', ',');
          hasUpdates = true;
        }
      }

      // Check for "Woninghuur - Provisies" product
      if (hasProvisionPrice && (
        productNameLower.includes("woninghuur - provisie") ||
        productNameLower.includes("- provisie")
      )) {
        // Only update if price is still 0 (not already set)
        if (product.basePrice === 0 && product.indexedPrice === 0) {
          updatedProducts[index] = {
            ...product,
            basePrice: advertisedProvisions,
            indexedPrice: advertisedProvisions
          };
          updatedPriceInputs[`${index}-basePrice`] = advertisedProvisions.toString().replace('.', ',');
          updatedPriceInputs[`${index}-indexedPrice`] = advertisedProvisions.toString().replace('.', ',');
          hasUpdates = true;
        }
      }
    });

    if (hasUpdates) {
      setSelectedProducts(updatedProducts);
      setPriceInputValues(updatedPriceInputs);
    }
  }, [isOpen, prefilledUnitId, selectedProducts.length, units]);

  const parseDecimalInput = (value: string): number => {
    // Support both comma and period as decimal separators
    if (!value) return 0;
    
    // Replace comma with period for parsing
    const normalizedValue = value.replace(',', '.');
    
    // Parse the number
    const number = parseFloat(normalizedValue);
    
    // Return 0 if parsing failed
    return isNaN(number) ? 0 : number;
  };

  const formatDecimalOutput = (value: number): string => {
    // Format for display - replace dot with comma for Dutch format
    return value.toString().replace('.', ',');
  };

  const handleProductChange = (
    index: number,
    field: keyof Product,
    value: any,
  ) => {
    if (field === "basePrice" || field === "indexedPrice") {
      // Store the raw input for display
      setPriceInputValues({
        ...priceInputValues,
        [`${index}-${field}`]: value
      });
      
      // Only update the actual product values if it's a valid number
      const parsedValue = parseDecimalInput(value);
      if (isNaN(parsedValue)) return;
      
      const updatedProducts = [...selectedProducts];
      updatedProducts[index] = {
        ...updatedProducts[index], 
        [field]: parsedValue
      };
      setSelectedProducts(updatedProducts);
      return;
    }
    
    // For non-price fields
    const updatedProducts = selectedProducts.map((product, i) => {
      if (i === index) {
        if (field === "name") {
          const template = productTemplates.find(
            (template) => template.name === value,
          );
          if (template) {
            return {
              name: template.name,
              basePrice: 0,
              indexedPrice: 0,
              templateId: template.id as number,
              ledger: template.ledger,
              indexable: template.indexable,
              odooProductVariantId: template.odooProductVariantId,
              odooTemplateName: template.odooTemplateName,
              archived: template.archived,
            };
          }
        }
        return { ...product, [field]: value };
      }
      return product;
    });
    setSelectedProducts(updatedProducts);
  };

  const handleAddProduct = () => {
    // Add a new product
    const newProducts = [
      ...selectedProducts,
      {
        name: "",
        basePrice: 0,
        indexedPrice: 0,
        templateId: null,
        ledger: null,
        indexable: false,
        odooProductVariantId: 0,
        odooTemplateName: null,
        archived: false,
        buildingAttributeId: null,
      },
    ];
    setSelectedProducts(newProducts);
    
    // Reset input values for the new product
    const newIndex = newProducts.length - 1;
    setPriceInputValues({
      ...priceInputValues,
      [`${newIndex}-basePrice`]: "0",
      [`${newIndex}-indexedPrice`]: "0"
    });
  };

  const handleAddBuildingAttribute = (attribute: BuildingAttribute) => {
    // Check if this attribute is already added
    const alreadyAdded = selectedProducts.some(
      product => product.buildingAttributeId === attribute.id
    );
    
    if (alreadyAdded) {
      toast.error(`${attribute.name} is al toegevoegd aan het contract`);
      return;
    }

    // Attributes on a non-closed contract stay selectable (e.g. a garage in opzeg needs a
    // follow-up contract), but make the overlap explicit so nobody double-rents by accident.
    const existingContract = getAttributeExistingContract(attribute);
    if (existingContract?.status === "rented") {
      toast.warn(
        `Let op: ${attribute.name} is nog gekoppeld aan een ${existingContract.phase === "In concept" ? "conceptcontract" : "lopend contract"}${existingContract.unitId ? ` (unit ${existingContract.unitId})` : ""}.`,
      );
    } else if (existingContract?.status === "termination") {
      toast.info(
        `${attribute.name} zit nog in opzeg${existingContract.unitId ? ` (unit ${existingContract.unitId})` : ""}. Het nieuwe contract kan aansluitend starten.`,
      );
    }

    // Find the matching product template for this company
    const matchingTemplate = attribute.template.productTemplates?.find(
      pt => pt.company.id === company.id
    );

    if (!matchingTemplate) {
      toast.error(`Geen product template gevonden voor ${attribute.template.category} voor dit bedrijf`);
      return;
    }

    // Find the full template from productTemplates to get all properties
    const fullTemplate = productTemplates.find(t => t.id === matchingTemplate.id);

    // Add the building attribute as a product with the correct product template name
    const newProduct: Product = {
      name: fullTemplate?.name || matchingTemplate.name, // Use the actual product template name
      basePrice: 0,
      indexedPrice: 0,
      templateId: matchingTemplate.id,
      ledger: fullTemplate?.ledger || attribute.template.ledger,
      indexable: fullTemplate?.indexable || true,
      odooProductVariantId: fullTemplate?.odooProductVariantId || null,
      odooTemplateName: fullTemplate?.odooTemplateName || matchingTemplate.name,
      archived: fullTemplate?.archived || false,
      buildingAttributeId: attribute.id,
    };

    setSelectedProducts([...selectedProducts, newProduct]);
    
    // Set initial price values
    const newIndex = selectedProducts.length;
    setPriceInputValues({
      ...priceInputValues,
      [`${newIndex}-basePrice`]: "0",
      [`${newIndex}-indexedPrice`]: "0"
    });
  };

  const handleRemoveProduct = (index: number) => {
    // Remove the product
    setSelectedProducts(selectedProducts.filter((_, i) => i !== index));
    
    // Clean up input values for this product
    const newPriceInputValues = { ...priceInputValues };
    delete newPriceInputValues[`${index}-basePrice`];
    delete newPriceInputValues[`${index}-indexedPrice`];
    
    // Update indices for products after the removed one
    const updatedPriceInputValues: { [key: string]: string } = {};
    Object.keys(newPriceInputValues).forEach(key => {
      const [productIndex, field] = key.split('-');
      const numIndex = parseInt(productIndex);
      
      if (numIndex > index) {
        // Decrement indices for products after the removed one
        updatedPriceInputValues[`${numIndex-1}-${field}`] = newPriceInputValues[key];
      } else {
        // Keep the same for products before the removed one
        updatedPriceInputValues[key] = newPriceInputValues[key];
      }
    });
    
    setPriceInputValues(updatedPriceInputValues);
  };

  const handleClose = () => {
    cleanUpFields();
    onClose();
  };

  // Toggle between a normal (unit-based) contract and an attribute-only contract (e.g. a parking
  // rented out on its own). Switching modes resets the selected products so we never mix a unit's
  // rental products with a standalone attribute line.
  const handleToggleAttributeOnly = (checked: boolean) => {
    setAttributeOnly(checked);
    setSelectedProducts([]);
    setPriceInputValues({});
    setBuildingAttributes([]);
    if (checked) {
      setContractDetails((prev) => ({
        ...prev,
        unitId: "",
        legalForm: ContractLegalForm.PARKING,
        invoicingFrequency: InvoicingFrequency.MONTHLY,
      }));
    } else {
      setContractDetails((prev) => ({ ...prev, legalForm: null }));
    }
  };

  const handleContractDetailsChange = (
    field: keyof RentalContract,
    value: any,
  ) => {
    if (field == "legalForm") {
      console.log("Product templates:");
      console.log(productTemplates);

      const createProductWithoutId = (template: any) => {
        // ommit id because else it will link to another product (that accidentally has the same id as the template id) in the backend
        const { id, ...rest } = template;
        return { ...rest, templateId: id, basePrice: 0, indexedPrice: 0 };
      };

      const sortProducts = (a: any, b: any) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        const aIsWoninghuurHuur =
          aName.includes("woninghuur - huur") ||
          aName.includes("Woninghuur - Huur") ||
          aName.includes("Woninghuur - huur") ||
          aName.includes("- Huur") ||
          aName.includes("- huur");
        const bIsWoninghuurHuur =
          bName.includes("woninghuur - huur") ||
          bName.includes("Woninghuur - Huur") ||
          bName.includes("Woninghuur - huur") ||
          bName.includes("- Huur") ||
          bName.includes("- huur");

        if (aIsWoninghuurHuur && !bIsWoninghuurHuur) return -1;
        if (!aIsWoninghuurHuur && bIsWoninghuurHuur) return 1;

        const aIsHuur = aName.includes("huur");
        const bIsHuur = bName.includes("huur");
        if (aIsHuur && !bIsHuur) return -1;
        if (!aIsHuur && bIsHuur) return 1;

        return 0;
      };

      // Reset price input values when changing contract type as we're setting new products
      setPriceInputValues({});

      // Defensive dedup by case-insensitive normalized name — protects against
      // duplicate templates in the productTemplates table (e.g., "Woninghuur - Provisies"
      // and "Woninghuur - provisies" both seeded for the same company).
      const dedupByName = <T extends { name?: string; id?: any }>(items: T[]): T[] => {
        const seen = new Map<string, T>();
        const duplicates: string[] = [];
        for (const item of items) {
          const key = (item.name ?? "").trim().toLowerCase();
          if (!key) continue;
          if (seen.has(key)) {
            duplicates.push(`${item.name} (id=${item.id})`);
            continue;
          }
          seen.set(key, item);
        }
        if (duplicates.length > 0) {
          console.warn(
            `[modalAddContract] Duplicate product templates ignored: ${duplicates.join(", ")}. Clean up the duplicates in the product-templates table.`,
          );
        }
        return Array.from(seen.values());
      };

      if (
        value == ContractLegalForm.RESIDENTIAL_SHORT_FLANDERS ||
        value == ContractLegalForm.RESIDENTIAL_LONG_FLANDERS
      ) {
        const residentialTemplates = productTemplates.filter(
          (template) => (template as any).type == "residential",
        );
        const products = dedupByName(residentialTemplates)
          .map(createProductWithoutId)
          .sort(sortProducts);
        console.log(products);
        setSelectedProducts(products);
      }

      if (
        value == ContractLegalForm.RETAIL_SHORT_FLANDERS ||
        value == ContractLegalForm.RETAIL_LONG_FLANDERS
      ) {
        const retailTemplates = productTemplates.filter(
          (template) => (template as any).type == "retail",
        );
        const products = dedupByName(retailTemplates)
          .map(createProductWithoutId)
          .sort(sortProducts);
        console.log(products);
        setSelectedProducts(products);
      }
    }

    setContractDetails((prev: any) => ({ ...prev, [field]: value }));
  };

  const RELATION_LABELS: Record<string, string> = {
    relationMe: 'Hoofdhuurder',
    relationSO: 'Partner',
    relationParent: 'Ouder',
    relationChild: 'Kind',
    relationSibling: 'Broer/zus',
    relationFriend: 'Vriend/in',
    relationGuarantor: 'Bewindvoerder of borgsteller',
    relationOther: 'Andere',
    relationNone: 'Geen',
  };

  const handleResidentsChange = (operation: any) => {
    if (operation === Operation.increment) {
      setNumResidents(numResidents + 1);
    } else {
      setNumResidents(numResidents - 1);
    }
  };

  const productTemplateOptions = Object.values(productTemplates).map(
    (value) => ({
      value: value.name,
      label: value.name,
    }),
  );

  // Contact type options for dropdown
  const contactTypeOptions = Object.values(ContactType).map((value) => ({
    value: value,
    label: value,
  }));

  // Contract contacts management functions
  const handleAddContractContact = () => {
    setContractContacts([
      ...contractContacts,
      { contactType: ContactType.TENANT, resourceId: null, resource: null }
    ]);
  };

  const handleRemoveContractContact = (index: number) => {
    setContractContacts(contractContacts.filter((_, i) => i !== index));
  };

  const handleContractContactTypeChange = (index: number, contactType: ContactType) => {
    const updated = [...contractContacts];
    updated[index] = { ...updated[index], contactType };
    setContractContacts(updated);
  };

  const handleContractContactSelect = (index: number, resource: any) => {
    const updated = [...contractContacts];
    updated[index] = { ...updated[index], resourceId: resource.id, resource };
    setContractContacts(updated);
  };

  const cleanUpFields = () => {
    setSelectedProducts([]);
    setBuildingAttributes([]);
    setPriceInputValues({});
    setAttributeOnly(attributeOnlyDefault);
    setContractDetails({
      legalForm: null,
      unitId: "",
      phase: ContractPhase.CONCEPT,
      contractContacts: [],
      fileLogIds: [],
      fileLogs: [],
      numResidents: 0,
      missingFileLogLabels: [],
    });
    setContractContacts([]);
    setSignDate(null);
    setStartDate(null);
    setEndDate(null);
    setNumResidents(1);
    setAvailableFileLogs([]);
    setSelectedFileLogIds([]);
    setEqualPriceConfirmed(false);
    setEqualPriceConfirmOpen(false);
    setEqualPriceProducts([]);
    setEqualPriceStartDateInfo({ startDate: null, daysSinceStart: null, yearsSinceStart: null, isSuspicious: false });
  };

  const handleFileLogSelection = (fileLogId: number, isChecked: boolean) => {
    if (isChecked) {
      setSelectedFileLogIds(prev => [...prev, fileLogId]);
    } else {
      setSelectedFileLogIds(prev => prev.filter(id => id !== fileLogId));
    }
  };

  const handleOpenFileLog = (fileLog: FileLog) => {
    if (fileLog.sharePointFileUrl) {
      window.open(fileLog.sharePointFileUrl, '_blank');
    }
  };

  const formatDate = (dateString: string | Date | null | undefined) => {
    if (!dateString) return 'Geen datum';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Alphanumeric sorting function
  const alphanumericSort = (a: string, b: string): number => {
    const aParts = a.split('-');
    const bParts = b.split('-');
    
    // Compare each part
    const maxLength = Math.max(aParts.length, bParts.length);
    
    for (let i = 0; i < maxLength; i++) {
      const aPart = aParts[i] || '';
      const bPart = bParts[i] || '';
      
      // Check if both parts are numeric
      const aIsNumeric = /^\d+$/.test(aPart);
      const bIsNumeric = /^\d+$/.test(bPart);
      
      if (aIsNumeric && bIsNumeric) {
        // Compare numerically
        const aNum = parseInt(aPart, 10);
        const bNum = parseInt(bPart, 10);
        if (aNum !== bNum) {
          return aNum - bNum;
        }
      } else {
        // Handle mixed alphanumeric strings like "G4", "G31"
        const aMatch = aPart.match(/^([A-Za-z]*)(\d*)(.*)$/);
        const bMatch = bPart.match(/^([A-Za-z]*)(\d*)(.*)$/);
        
        if (aMatch && bMatch) {
          const [, aText, aNumber, aRest] = aMatch;
          const [, bText, bNumber, bRest] = bMatch;
          
          // Compare text parts first
          if (aText !== bText) {
            return aText.localeCompare(bText);
          }
          
          // Compare numeric parts
          if (aNumber && bNumber) {
            const aNum = parseInt(aNumber, 10);
            const bNum = parseInt(bNumber, 10);
            if (aNum !== bNum) {
              return aNum - bNum;
            }
          } else if (aNumber && !bNumber) {
            return 1; // Numbers come after non-numbers
          } else if (!aNumber && bNumber) {
            return -1; // Non-numbers come before numbers
          }
          
          // Compare remaining parts
          if (aRest !== bRest) {
            return aRest.localeCompare(bRest);
          }
        } else {
          // Fallback to alphabetical comparison
          const comparison = aPart.localeCompare(bPart);
          if (comparison !== 0) {
            return comparison;
          }
        }
      }
    }
    
    return 0;
  };

  const formatUnitId = (unitId: string) => {
    // Split by dash and check if there are more than 2 numbers at the end
    const parts = unitId.split('-');
    
    // Find all numeric parts
    const numericParts: { part: string; index: number }[] = [];
    const nonNumericParts: { part: string; index: number }[] = [];
    
    parts.forEach((part, index) => {
      if (/^\d+$/.test(part)) {
        numericParts.push({ part, index });
      } else {
        nonNumericParts.push({ part, index });
      }
    });
    
    // If we have more than 2 numeric parts, highlight the last 2
    if (numericParts.length > 2) {
      const lastNumericIndex = numericParts[numericParts.length - 1].index;
      const secondLastNumericIndex = numericParts[numericParts.length - 2].index;
      
      return (
        <span>
          {parts.map((part, index) => {
            let className = '';
            let key = `${unitId}-${index}`;
            
            if (index === secondLastNumericIndex) {
              className = 'text-brick-primary font-semibold';
            } else if (index === lastNumericIndex) {
              className = 'text-moss-primary font-semibold';
            }
            
            return (
              <span key={key}>
                <span className={className}>{part}</span>
                {index < parts.length - 1 && '-'}
              </span>
            );
          })}
        </span>
      );
    }
    
    // If 2 or fewer numeric parts, return normal
    return <span>{unitId}</span>;
  };

  // Function to get display text for unit (for dropdown display)
  const getUnitDisplayText = (unit: any) => {
    return unit.id + (unit.referencePlus ? " (" + unit.referencePlus + ")" : "");
  };

  const handleCreateContract = async () => {
    if (isSaving) return; // avoid double saving
    setIsSaving(true);
    try {
      // Filter out contacts without a selected resource id (must be a number) and map to the expected format
      const validContacts = contractContacts
        .filter(c => c.resourceId !== null && c.contactType !== null && typeof c.resourceId === 'number')
        .map(c => ({
          contactType: c.contactType,
          resourceId: c.resourceId,
        }));

      const contract = {
        ...contractDetails,
        // Attribute-only contracts have no unit; they are scoped to the building instead.
        unitId: attributeOnly ? undefined : contractDetails.unitId,
        buildingId: attributeOnly ? building?.id : undefined,
        companyId: company.id,
        odooCompanyId: company.odooId,
        signDate: signDate,
        startDate: startDate,
        endDate: endDate,
        // Keep counterparties for backward compatibility with backend
        counterparties: validContacts.map(c => ({ id: c.resourceId })),
        // Also send the full contract contacts with types
        contractContacts: validContacts,
        numResidents: numResidents,
        products: selectedProducts,
        pushContractToOdoo: true,
        fileLogIds: selectedFileLogIds,
      };

      if (!selectedProducts || selectedProducts.length === 0) {
        toast.error("Minstens één product is verplicht");
        setIsSaving(false);
        return;
      }

      if (!selectedProducts.every((product) => product.name !== "")) {
        toast.error("Productnaam is verplicht voor elk product");
        setIsSaving(false);
        return;
      }

      // Check base price validation - building attributes can be 0, regular products must be > 0
      if (!selectedProducts.every((product) => {
        if (product.buildingAttributeId) {
          return product.basePrice >= 0; // Building attributes can be 0
        } else {
          return product.basePrice > 0; // Regular products must be > 0
        }
      })) {
        toast.error("Basisprijs moet groter zijn dan 0 voor reguliere producten, gebouw attributen mogen 0 zijn");
        setIsSaving(false);
        return;
      }

      // Check indexed price validation - building attributes can be 0, regular products must be > 0
      if (!selectedProducts.every((product) => {
        if (product.buildingAttributeId) {
          return product.indexedPrice >= 0; // Building attributes can be 0
        } else {
          return product.indexedPrice > 0; // Regular products must be > 0
        }
      })) {
        toast.error("Geïndexeerde prijs moet groter zijn dan 0 voor reguliere producten, gebouw attributen mogen 0 zijn");
        setIsSaving(false);
        return;
      }

      if (validContacts.length === 0) {
        toast.error("Tenminste één contact met type aanduiden is verplicht");
        setIsSaving(false);
        return;
      }

      if (!selectedProducts.every((product) => product.templateId != 0)) {
        toast.error("Product template niet gekoppeld. Contacteer Bjorn");
        setIsSaving(false);
        return;
      }

      if (
        !selectedProducts.every(
          (product) => product.odooProductVariantId != null,
        )
      ) {
        toast.error("Odoo product variant id niet gekoppeld. Contacteer Bjorn");
        setIsSaving(false);
        return;
      }

      if (
        !selectedProducts.every((product) => product && product.templateId != 0)
      ) {
        toast.error("Product template niet gekoppeld. Contacteer Bjorn");
        setIsSaving(false);
        return;
      }

      if (
        !selectedProducts.every(
          (product) => product.indexedPrice >= product.basePrice,
        )
      ) {
        toast.error(
          "Basisprijs per maand mag niet hoger zijn dan de geïndexeerde prijs per maand",
        );
        setIsSaving(false);
        return;
      }

      if (contract.startDate == null) {
        toast.error("Startdatum is verplicht");
        setIsSaving(false);
        return;
      }

      if (!attributeOnly && contract.signDate == null) {
        toast.error("Ondertekeningsdatum is verplicht");
        setIsSaving(false);
        return;
      }

      if (
        contract.legalForm != ContractLegalForm.RETAIL_SHORT_FLANDERS &&
        contract.legalForm != ContractLegalForm.RETAIL_LONG_FLANDERS &&
        contract.legalForm != ContractLegalForm.PARKING &&
        contract.endDate == null
      ) {
        toast.error("Einddatum is verplicht");
        setIsSaving(false);
        return;
      }

      if (attributeOnly) {
        // Attribute-only contract: no unit, but we need a building and at least one attribute line.
        if (!building?.id) {
          toast.error("Geen gebouw bekend voor dit gebouwattribuut-contract");
          setIsSaving(false);
          return;
        }
        if (!selectedProducts.some((product) => !!product.buildingAttributeId)) {
          toast.error("Selecteer minstens één gebouwattribuut om te verhuren");
          setIsSaving(false);
          return;
        }
      } else if (contract.unitId == "" || !contract.unitId) {
        toast.error("Unit is verplicht");
        setIsSaving(false);
        return;
      }

      if (contract.legalForm == null) {
        toast.error("Contract type is verplicht");
        setIsSaving(false);
        return;
      }

      if (!contract.invoicingFrequency) {
        toast.error("Facturatiefrequentie is verplicht");
        setIsSaving(false);
        return;
      }

      if (validContacts.length == 0) {
        toast.error("Contacten zijn verplicht");
        setIsSaving(false);
        return;
      }

      if (
        contract.endDate &&
        contract.startDate &&
        contract.endDate < contract.startDate
      ) {
        toast.error("Einddatum moet na startdatum zijn");
        setIsSaving(false);
        return;
      }

      // Soft warning: any product with basePrice === indexedPrice (both > 0)?
      // User can still proceed, but we want to flag the likely data-entry mistake
      // because it breaks future indexation calculations.
      const offendingProducts = selectedProducts
        .filter(
          (product) =>
            product.basePrice > 0 &&
            product.indexedPrice > 0 &&
            product.basePrice === product.indexedPrice,
        )
        .map((product, index) => ({
          name: product.name?.trim() || `Product ${index + 1}`,
          price: product.basePrice,
        }));
      if (offendingProducts.length > 0 && !equalPriceConfirmed) {
        // Extra context: equal prices on a contract that started >= 1 year ago
        // suggests skipped indexations in the past — much higher chance of data error.
        // < 1 year is plausibly OK; we show a green note explaining that.
        const startDateInfo = (() => {
          if (!contract.startDate) {
            return {
              startDate: null,
              daysSinceStart: null,
              yearsSinceStart: null,
              isSuspicious: false,
            };
          }
          const start = new Date(contract.startDate);
          if (isNaN(start.getTime())) {
            return {
              startDate: null,
              daysSinceStart: null,
              yearsSinceStart: null,
              isSuspicious: false,
            };
          }
          const msPerDay = 1000 * 60 * 60 * 24;
          const daysSinceStart = Math.floor((Date.now() - start.getTime()) / msPerDay);
          const yearsSinceStart = Math.floor(daysSinceStart / 365);
          return {
            startDate: start,
            daysSinceStart,
            yearsSinceStart,
            isSuspicious: daysSinceStart >= 365,
          };
        })();
        setEqualPriceProducts(offendingProducts);
        setEqualPriceStartDateInfo(startDateInfo);
        setEqualPriceConfirmOpen(true);
        setIsSaving(false);
        return;
      }

      console.log("Creating contract:");
      console.log(contract);

      try {
        const res = await fetchWithAuth(
          `${API_URL}/rental/contracts/create`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(contract),
          },
          session,
        );

        const data = await res.json().catch(() => null);
        console.log(data);

        if (!res.ok) {
          // Surface the backend's actual error message instead of a generic one.
          const message = Array.isArray(data?.message)
            ? data.message.join(" ")
            : data?.message || `Aanmaken mislukt (status ${res.status}).`;
          toast.error(`Contract niet aangemaakt: ${message}`, { autoClose: false });
          return; // keep the modal open so the user can correct/retry
        }

        if (data?.odooError) {
          // The contract was saved locally, but the Odoo sync failed — tell the user exactly why.
          toast.warn(
            `Contract lokaal aangemaakt, maar synchronisatie met Odoo is mislukt: ${data.odooError}`,
            { autoClose: false },
          );
        } else {
          toast.success("Contract succesvol aangemaakt");
        }

        cleanUpFields();
        refreshContracts && refreshContracts();
        onClose();
      } catch (error: any) {
        console.error("Error creating contract:", error);
        toast.error(`Fout bij aanmaken van contract: ${error?.message || "onbekende fout"}`, {
          autoClose: false,
        });
      }
    } catch (error) {
      console.error("Error creating contract:", error);
      toast.error("Error creating contract. Contact Bjorn");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetStartDate = (date: Date) => {
    setStartDate(date);
    // when residential contract of short length, set end date to 1 year later minus one day
    if (
      contractDetails.legalForm == ContractLegalForm.RESIDENTIAL_SHORT_FLANDERS
    ) {
      const endDate = new Date(date);
      endDate.setFullYear(endDate.getFullYear() + 1);
      endDate.setDate(endDate.getDate() - 1);
      setEndDate(endDate);
    }

    // when residential contract of long length, set end date to 9 years later minus one day
    if (
      contractDetails.legalForm == ContractLegalForm.RESIDENTIAL_LONG_FLANDERS
    ) {
      const endDate = new Date(date);
      endDate.setFullYear(endDate.getFullYear() + 9);
      endDate.setDate(endDate.getDate() - 1);
      setEndDate(endDate);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="space-y-6">
            {/* Basic Contract Information */}
            <div className="space-y-4 rounded-2xl border border-neutral-primary/10 bg-white p-5 shadow-sm sm:p-6">
              <h3 className="text-base font-semibold text-neutral-primary">
                Contract informatie
              </h3>

              {/* Attribute-only toggle: rent out a standalone building attribute (e.g. a parking) without a unit. */}
              {building?.id && (
                <div className="flex items-start gap-3 p-3 rounded-xl border border-neutral-primary/10 bg-brick-secondary-fine/40">
                  <Checkbox
                    isSelected={attributeOnly}
                    onValueChange={handleToggleAttributeOnly}
                  />
                  <div>
                    <p className="text-sm font-medium text-neutral-primary">
                      Verhuur zonder unit (enkel gebouwattribuut)
                    </p>
                    <p className="text-xs text-neutral-secondary-mid">
                      Maak een contract voor een los gebouwattribuut, zoals een parkeerplaats, zonder gekoppelde unit.
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <span className="text-sm text-neutral-primary">Bedrijf</span>
                  {company && (
                    <div className="flex gap-2 items-center rounded-xl bg-brick-secondary-fine/40 p-3">
                      <Education />
                      <span className="text-neutral-primary">{company.name}</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-sm text-neutral-primary">
                    {attributeOnly ? "Gebouw" : "Unit"}
                  </span>
                  {attributeOnly ? (
                    <div className="flex gap-2 items-center rounded-xl bg-brick-secondary-fine/40 p-3">
                      <Education />
                      <span className="text-neutral-primary">
                        {building?.id || "Onbekend gebouw"}
                      </span>
                    </div>
                  ) : (
                    <>
                      <EditableSelectComponent
                        options={
                          units &&
                          units
                            .sort((a, b) => alphanumericSort(a.id || '', b.id || ''))
                            .map(
                              (unit) =>
                                (({
                                  label: getUnitDisplayText(unit),
                                  value: unit.id
                                }) as any),
                            )
                        }
                        value={contractDetails.unitId as string}
                        isLoading={false}
                        editMode={true}
                        onChange={(value) =>
                          handleContractDetailsChange("unitId", value)
                        }
                      />
                      {contractDetails.unitId && (
                        <div className="text-xs text-neutral-secondary-mid">
                          <span className="font-medium">Unit ID: </span>
                          {formatUnitId(contractDetails.unitId)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {wizardStep === "method" ? (
              <ContractWizardMethodStep
                ctx={{
                  companyId: company.id,
                  unitId: contractDetails.unitId || undefined,
                  buildingId: attributeOnly ? building?.id || undefined : undefined,
                  userId: user?.id,
                }}
                session={session}
                onExtracted={handleExtractedContract}
                onDocumentReady={(doc) => onPreviewDocument?.(doc)}
                huurcontractFileLogs={huurcontractFileLogs}
                isLoadingFileLogs={isLoadingFileLogs}
                analyzingFileLogId={analyzingFileLogId}
                onAnalyzeExisting={handleAnalyzeExistingFileLog}
                onManual={() => setWizardStep("form")}
                disabled={!attributeOnly && !contractDetails.unitId}
                disabledReason="Selecteer eerst een unit hierboven om het huurcontract uit te lezen."
              />
            ) : (
              <>
            {extractionReview && (
              <ExtractionReviewBanner
                lowConfidenceFields={extractionReview.lowConfidenceFields}
                warnings={extractionReview.warnings}
              />
            )}

            {/* Contract type */}
            <div className="space-y-4 rounded-2xl border border-neutral-primary/10 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-moss-primary/10 text-sm font-semibold text-moss-primary">1</span>
                <div>
                  <h3 className="text-base font-semibold text-neutral-primary">Contracttype</h3>
                  <p className="text-xs text-neutral-secondary-mid">Juridisch regime van de huurovereenkomst</p>
                </div>
              </div>
              <div className="max-w-md">
                <EditableSelectComponent
                  options={Object.values(ContractLegalForm).map(
                    (type) => (({ label: type, value: type }) as any),
                  )}
                  value={contractDetails.legalForm as ContractLegalForm}
                  isLoading={false}
                  editMode={true}
                  onChange={(value) =>
                    handleContractDetailsChange("legalForm", value)
                  }
                />
              </div>
            </div>

            {/* Tenants Section */}
            <div className="space-y-4 rounded-2xl border border-neutral-primary/10 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-moss-primary/10 text-sm font-semibold text-moss-primary">2</span>
                  <h3 className="text-base font-semibold text-neutral-primary">Contactpersonen</h3>
                </div>
                <ButtonNew
                  onClick={handleAddContractContact}
                  icon={<Plus />}
                  label="Contact toevoegen"
                  variant="primaryMoss"
                />
              </div>

              <span className="text-red-500 font-semibold text-sm">
                Rijksregisternummer handmatig invoeren bij contact! (calendar app doet die niet automatisch)
              </span>

              <div className="space-y-3">
                {contractContacts.length === 0 && (
                  <div className="flex items-center gap-3 rounded-xl border border-neutral-primary/10 bg-neutral-secondary-mid/[0.06] p-3.5">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-neutral-secondary-mid/15">
                      <span className="text-neutral-primary text-xs">ℹ</span>
                    </div>
                    <div>
                      <p className="text-neutral-primary font-medium text-sm">Geen contacten toegevoegd</p>
                      <p className="text-neutral-secondary-mid text-xs">
                        Voeg minstens één huurder toe via de knop "Contact toevoegen".
                      </p>
                    </div>
                  </div>
                )}

                {contractContacts.map((contact, index) => {
                  const rd = contact.residentData;
                  return (
                  <div key={index} className="p-4 rounded-xl border border-neutral-primary/10 bg-brick-secondary-fine/40 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-4 items-end">
                      <EditableSelectComponent
                        label="Type contact"
                        editMode={true}
                        isLoading={false}
                        options={contactTypeOptions}
                        value={contact.contactType as string}
                        onChange={(value) => handleContractContactTypeChange(index, value as ContactType)}
                      />
                      {contact.resourceId ? (
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-neutral-primary">Contact</span>
                          <div className="flex items-center gap-2 rounded-sm bg-white p-2.5 shadow-form-element">
                            <span className="flex-1 text-sm text-neutral-primary">{contact.resource?.name}</span>
                            {contact.wasExisting !== undefined && (
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${contact.wasExisting ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                {contact.wasExisting ? 'Bestaat al in database' : 'Nieuw contact'}
                              </span>
                            )}
                            {contact.resource?.resourceId && (
                              <button
                                type="button"
                                title="Open contact in nieuw tabblad"
                                onClick={() => window.open(`/contact/${contact.resource.resourceId}`, "_blank")}
                                className="text-neutral-secondary-mid transition-colors hover:text-moss-primary"
                              >
                                <ArrowSquareOut size={16} />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...contractContacts];
                                updated[index] = { ...updated[index], resourceId: null, resource: null };
                                setContractContacts(updated);
                              }}
                              className="text-neutral-secondary-mid hover:text-red-500 transition-colors text-xs"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <SearchComponent
                          type="regular"
                          label="Zoek contact of bedrijf"
                          resourceFilter={[ResourceType.CONTACT, ResourceType.COMPANY]}
                          initialValue=""
                          onSelect={(resource) => handleContractContactSelect(index, resource)}
                          mode={SearchMode.SEARCH_AND_PRESENT}
                          editMode={true}
                        />
                      )}
                      <ButtonNew
                        icon={<Trash />}
                        variant="secondaryBrick"
                        onClick={() => handleRemoveContractContact(index)}
                        label="Verwijderen"
                      />
                    </div>
                    {rd && (
                      <div className="p-3 bg-white rounded border border-neutral-primary/10 grid grid-cols-2 gap-x-6 gap-y-1.5">
                        {rd.email && (
                          <span className="text-xs text-neutral-secondary-mid col-span-2">
                            <span className="font-medium text-neutral-primary">E-mail:</span> {rd.email}
                          </span>
                        )}
                        {rd.phoneNumber && (
                          <span className="text-xs text-neutral-secondary-mid">
                            <span className="font-medium text-neutral-primary">Telefoon:</span> {rd.phoneNumber}
                          </span>
                        )}
                        {rd.nationality && (
                          <span className="text-xs text-neutral-secondary-mid">
                            <span className="font-medium text-neutral-primary">Nationaliteit:</span> {rd.nationality}
                          </span>
                        )}
                        {rd.spokenLanguages && rd.spokenLanguages.length > 0 && (
                          <span className="text-xs text-neutral-secondary-mid">
                            <span className="font-medium text-neutral-primary">Talen:</span> {rd.spokenLanguages.join(', ')}
                          </span>
                        )}
                        {rd.dateOfBirth && (
                          <span className="text-xs text-neutral-secondary-mid">
                            <span className="font-medium text-neutral-primary">Geboortedatum:</span> {new Date(rd.dateOfBirth).toLocaleDateString('nl-NL')}
                          </span>
                        )}
                        {rd.placeOfBirth && (
                          <span className="text-xs text-neutral-secondary-mid">
                            <span className="font-medium text-neutral-primary">Geboorteplaats:</span> {rd.placeOfBirth}
                          </span>
                        )}
                        {rd.relationToApplicant && (
                          <span className="text-xs text-neutral-secondary-mid col-span-2">
                            <span className="font-medium text-neutral-primary">Relatie tot aanvrager:</span> {RELATION_LABELS[rd.relationToApplicant] ?? rd.relationToApplicant}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>

              <div className="flex gap-2 pt-2">
                <CounterField
                  key={numResidents}
                  min={1}
                  defaultCount={numResidents}
                  handleCountChange={handleResidentsChange}
                  label="Aantal bewoners"
                />
              </div>
            </div>

            {/* Duration Section */}
            <div className="space-y-4 rounded-2xl border border-neutral-primary/10 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-moss-primary/10 text-sm font-semibold text-moss-primary">3</span>
                <div>
                  <h3 className="text-base font-semibold text-neutral-primary">Looptijd &amp; facturatie</h3>
                  <p className="text-xs text-neutral-secondary-mid">Ondertekening, start, einde en facturatieritme</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col gap-2">
                  <span className="text-sm text-neutral-primary">Ondertekeningsdatum</span>
                  <DatePicker
                    selected={signDate ? new Date(signDate) : null}
                    onChange={(date: any) => setSignDate(date as Date)}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Selecteer datum"
                    className="w-full rounded-xl border border-neutral-primary/15 bg-white px-3.5 py-2.5 text-neutral-primary outline-none transition placeholder:text-neutral-secondary-mid focus:border-moss-primary focus:ring-2 focus:ring-moss-primary/20"
                  />
                  <span className="text-xs text-neutral-secondary-mid">
                    Gebruikt voor indexatieberekening
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-sm text-neutral-primary">Startdatum</span>
                  <DatePicker
                    selected={startDate ? new Date(startDate) : null}
                    onChange={(date: any) => handleSetStartDate(date as Date)}
                    dateFormat="dd/MM/yyyy"
                    className="w-full rounded-xl border border-neutral-primary/15 bg-white px-3.5 py-2.5 text-neutral-primary outline-none transition placeholder:text-neutral-secondary-mid focus:border-moss-primary focus:ring-2 focus:ring-moss-primary/20"
                  />
                </div>
                {contractDetails.legalForm !=
                  ContractLegalForm.RETAIL_LONG_FLANDERS && (
                  <div className="flex flex-col gap-2">
                    <span className="text-sm text-neutral-primary">Einddatum</span>
                    <DatePicker
                      selected={endDate ? new Date(endDate) : null}
                      onChange={(date: any) => setEndDate(date as Date)}
                      dateFormat="dd/MM/yyyy"
                      className="w-full rounded-xl border border-neutral-primary/15 bg-white px-3.5 py-2.5 text-neutral-primary outline-none transition placeholder:text-neutral-secondary-mid focus:border-moss-primary focus:ring-2 focus:ring-moss-primary/20"
                    />
                  </div>
                )}
              </div>

              {contractDetails.legalForm ==
                ContractLegalForm.RETAIL_LONG_FLANDERS && (
                <p className="text-sm text-neutral-secondary-mid rounded-xl bg-brick-secondary-fine/40 p-3">
                  Voor retail contracten van lange duur zet het systeem de
                  einddatum automatisch na 36 jaar, met daarbinnen 3
                  hernieuwingsperiodes
                </p>
              )}

              <div className="flex flex-col gap-2">
                <span className="text-sm text-neutral-primary">
                  Facturatiefrequentie <span className="text-red-500">*</span>
                </span>
                <EditableSelectComponent
                  options={Object.values(InvoicingFrequency).map(
                    (frequency: any) =>
                      (({
                        label: frequency,
                        value: frequency
                      }) as any),
                  )}
                  value={contractDetails.invoicingFrequency as string}
                  isLoading={false}
                  label="Facturatiefrequentie"
                  editMode={true}
                  onChange={(value) =>
                    handleContractDetailsChange("invoicingFrequency", value)
                  }
                />
              </div>
            </div>

            {/* Building Attributes Section */}
            <div className="space-y-4 rounded-2xl border border-neutral-primary/10 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-moss-primary/10 text-sm font-semibold text-moss-primary">4</span>
                <div>
                  <h3 className="text-base font-semibold text-neutral-primary">
                    {attributeOnly ? "Gebouwattributen" : "Gebouwattributen"}
                  </h3>
                  <p className="text-xs text-neutral-secondary-mid">
                    {attributeOnly ? "Verhuurde losse attributen" : "Optioneel — bv. garagebox of parkeerplaats"}
                  </p>
                </div>
              </div>

              {(!contractDetails.legalForm || (!attributeOnly && !contractDetails.unitId)) && (
                <div className="flex items-center gap-3 rounded-xl border border-neutral-primary/10 bg-neutral-secondary-mid/[0.06] p-3.5">
                  <div className="w-6 h-6 bg-neutral-secondary-mid/20 rounded-full flex items-center justify-center">
                    <span className="text-neutral-primary text-xs">⚠</span>
                  </div>
                  <div>
                    <p className="text-neutral-primary font-medium text-sm">
                      {attributeOnly ? "Eerst contract type selecteren" : "Eerst contract type en unit selecteren"}
                    </p>
                    <p className="text-neutral-secondary-mid text-xs">
                      {attributeOnly
                        ? "Selecteer eerst een contract type hierboven om beschikbare gebouw attributen te zien."
                        : "Selecteer eerst een contract type en unit hierboven om beschikbare gebouw attributen te zien."}
                    </p>
                  </div>
                </div>
              )}

              {contractDetails.legalForm && (attributeOnly || contractDetails.unitId) && buildingAttributes.length === 0 && (
                <div className="flex items-center gap-3 rounded-xl border border-neutral-primary/10 bg-neutral-secondary-mid/[0.06] p-3.5">
                  <div className="w-6 h-6 bg-neutral-secondary-mid/20 rounded-full flex items-center justify-center">
                    <span className="text-neutral-primary text-xs">ℹ</span>
                  </div>
                  <div>
                    <p className="text-neutral-primary font-medium text-sm">Geen attributen gevonden</p>
                    <p className="text-neutral-secondary-mid text-xs">
                      {attributeOnly
                        ? "Er zijn geen gebouwattributen gevonden voor dit gebouw."
                        : "Er zijn geen gebouwattributen gevonden voor deze unit."}
                    </p>
                  </div>
                </div>
              )}

              {contractDetails.legalForm && (attributeOnly || contractDetails.unitId) && buildingAttributes.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 rounded-xl border border-moss-primary/20 bg-moss-primary/5 p-3">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-moss-primary/15">
                      <span className="text-moss-primary text-xs">✓</span>
                    </div>
                    <div>
                      <p className="text-neutral-primary font-medium text-sm">{buildingAttributes.length} attributen gevonden</p>
                      <p className="text-neutral-secondary-mid text-xs">
                        Klik op een attribuut hieronder om het toe te voegen aan het contract. Attributen met een
                        lopend contract of in opzeg tonen hun huidige contract.
                      </p>
                    </div>
                  </div>
                  
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {buildingAttributes
                      .filter(attribute => !selectedProducts.some(product => product.buildingAttributeId === attribute.id))
                      .sort((a, b) => alphanumericSort(a.name, b.name))
                      .map((attribute) => (
                        <div
                          key={attribute.id}
                          className="group flex items-center justify-between p-3 rounded-xl border border-neutral-primary/10 bg-brick-secondary-fine/40 hover:border-moss-primary hover:shadow-sm cursor-pointer transition-all duration-200"
                          onClick={() => handleAddBuildingAttribute(attribute)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="font-medium text-neutral-primary text-sm">
                                {attribute.name}
                              </div>
                              <div className="px-2 py-1 bg-brick-primary/10 text-brick-primary rounded-sm text-xs">
                                {attribute.template.category}
                              </div>
                            </div>
                            <div className="text-xs text-neutral-secondary-mid">
                              Grootboek: {attribute.template.ledger}
                              {attribute.description && ` • ${attribute.description}`}
                            </div>
                            <AttributeExistingContractBadge attribute={attribute} className="mt-1.5" />
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-xs text-moss-primary font-medium">
                              Toevoegen
                            </div>
                            <Plus className="text-moss-primary w-4 h-4 group-hover:scale-110 transition-transform" />
                          </div>
                        </div>
                      ))}
                  </div>
                  
                  {buildingAttributes.filter(attribute => !selectedProducts.some(product => product.buildingAttributeId === attribute.id)).length === 0 && (
                    <div className="rounded-xl border border-moss-primary/20 bg-moss-primary/5 py-4 text-center">
                      <div className="text-moss-primary mb-1 text-sm">✓</div>
                      <p className="text-xs text-neutral-primary">
                        Alle attributen zijn al toegevoegd aan dit contract
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Products Section */}
            <div className="space-y-4 rounded-2xl border border-neutral-primary/10 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-moss-primary/10 text-sm font-semibold text-moss-primary">5</span>
                  <div>
                    <h3 className="text-base font-semibold text-neutral-primary">Huurprijs &amp; producten</h3>
                    <p className="text-xs text-neutral-secondary-mid">Basishuur, provisies en overige producten</p>
                  </div>
                </div>
                <ButtonNew
                  onClick={handleAddProduct}
                  icon={<Plus />}
                  label="Product toevoegen"
                  variant="primaryMoss"
                />
              </div>
              
              <div className="space-y-3">
                {selectedProducts.map((product, index) => (
                  <div
                    key={index}
                    className={`relative rounded-xl border p-5 ${product.buildingAttributeId ? "border-brick-primary/30 bg-brick-primary/5" : "border-neutral-primary/10 bg-brick-secondary-fine/40"}`}
                  >
                    {/* Delete as a subtle corner icon instead of a dominant button */}
                    <button
                      type="button"
                      aria-label="Product verwijderen"
                      onClick={() => handleRemoveProduct(index)}
                      className="absolute right-3 top-3 rounded-md p-1.5 text-brick-primary/70 transition-colors hover:bg-brick-primary/10 hover:text-brick-primary"
                    >
                      <Trash size={18} />
                    </button>

                    {product.buildingAttributeId && (
                      <div className="mb-3 flex items-center gap-2 text-xs text-brick-primary">
                        <div className="h-2 w-2 rounded-full bg-brick-primary"></div>
                        <span className="font-medium">
                          Gebouw attribuut: {buildingAttributes.find(attr => attr.id === product.buildingAttributeId)?.name || 'Onbekend'}
                        </span>
                      </div>
                    )}

                    <div className="space-y-4 pr-8">
                      {/* Product on its own full-width row so the name never truncates */}
                      <EditableSelectComponent
                        key={index}
                        label="Product"
                        editMode={true}
                        isLoading={false}
                        options={productTemplateOptions}
                        value={product.name}
                        onChange={(value) =>
                          handleProductChange(index, "name", value)
                        }
                        isDisabled={!!product.buildingAttributeId}
                      />

                      <div className="flex flex-wrap items-end gap-4">
                        <div className="min-w-[150px] flex-1">
                          <EditableInputComponent
                            label="Basisprijs / maand (€)"
                            editMode={true}
                            isLoading={false}
                            value={priceInputValues[`${index}-basePrice`] !== undefined
                              ? priceInputValues[`${index}-basePrice`]
                              : formatDecimalOutput(product.basePrice)}
                            onChange={(value) =>
                              handleProductChange(index, "basePrice", value)
                            }
                          />
                        </div>
                        <div className="min-w-[150px] flex-1">
                          <EditableInputComponent
                            label="Geïndexeerd / maand (€)"
                            editMode={true}
                            isLoading={false}
                            value={priceInputValues[`${index}-indexedPrice`] !== undefined
                              ? priceInputValues[`${index}-indexedPrice`]
                              : formatDecimalOutput(product.indexedPrice)}
                            onChange={(value) =>
                              handleProductChange(index, "indexedPrice", value)
                            }
                          />
                        </div>

                        {/* BTW: a readable segmented toggle (0% / 21%) instead of a cramped dropdown */}
                        <div className="flex flex-col gap-1.5">
                          <span className="text-sm text-neutral-primary">BTW</span>
                          <div className="inline-flex rounded-lg border border-neutral-primary/20 bg-white p-0.5">
                            {[0, 21].map((rate) => (
                              <button
                                key={rate}
                                type="button"
                                onClick={() => handleProductChange(index, "vatRate", rate)}
                                className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                                  Number(product.vatRate ?? 0) === rate
                                    ? "bg-moss-primary text-white"
                                    : "text-neutral-secondary-mid hover:text-neutral-primary"
                                }`}
                              >
                                {rate}%
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {selectedProducts.length > 0 && (
                <IndexedRentNotice
                  signDate={signDate}
                  startDate={startDate}
                  epc={unitEpc}
                  products={selectedProducts.map((p) => ({
                    name: p.name,
                    basePrice: Number(p.basePrice) || 0,
                  }))}
                />
              )}
            </div>

            {/* Existing Contract Documents Section */}
            <div className="space-y-4 rounded-2xl border border-neutral-primary/10 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-moss-primary/10 text-sm font-semibold text-moss-primary">6</span>
                <div>
                  <h3 className="text-base font-semibold text-neutral-primary">Documenten</h3>
                  <p className="text-xs text-neutral-secondary-mid">Koppel bewijsstukken aan dit contract</p>
                </div>
              </div>
              
              {(!contractDetails.unitId && !attributeOnly) && (
                <div className="flex items-center gap-3 rounded-xl border border-neutral-primary/10 bg-neutral-secondary-mid/[0.06] p-3.5">
                  <div className="w-6 h-6 bg-neutral-secondary-mid/20 rounded-full flex items-center justify-center">
                    <span className="text-neutral-primary text-xs">⚠</span>
                  </div>
                  <div>
                    <p className="text-neutral-primary font-medium text-sm">Eerst unit selecteren</p>
                    <p className="text-neutral-secondary-mid text-xs">
                      Selecteer eerst een unit hierboven om bestaande documenten te zien.
                    </p>
                  </div>
                </div>
              )}
              
              {(contractDetails.unitId || attributeOnly) && isLoadingFileLogs && (
                <div className="flex items-center gap-3 rounded-xl border border-neutral-primary/10 bg-neutral-secondary-mid/[0.06] p-3.5">
                  <div className="w-6 h-6 bg-neutral-secondary-mid/20 rounded-full flex items-center justify-center">
                    <span className="text-neutral-primary text-xs">⏳</span>
                  </div>
                  <div>
                    <p className="text-neutral-primary font-medium text-sm">Documenten laden...</p>
                    <p className="text-neutral-secondary-mid text-xs">
                      Zoeken naar huurcontract-gerelateerde documenten van de laatste 4 maanden.
                    </p>
                  </div>
                </div>
              )}
              
              {(contractDetails.unitId || attributeOnly) && !isLoadingFileLogs && availableFileLogs.length === 0 && (
                <div className="flex items-center gap-3 rounded-xl border border-neutral-primary/10 bg-neutral-secondary-mid/[0.06] p-3.5">
                  <div className="w-6 h-6 bg-neutral-secondary-mid/20 rounded-full flex items-center justify-center">
                    <span className="text-neutral-primary text-xs">ℹ</span>
                  </div>
                  <div>
                    <p className="text-neutral-primary font-medium text-sm">Geen documenten gevonden</p>
                    <p className="text-neutral-secondary-mid text-xs">
                      Er zijn geen huurcontract-gerelateerde documenten gevonden voor deze unit in de laatste 4 maanden.
                    </p>
                  </div>
                </div>
              )}
              
              {(contractDetails.unitId || attributeOnly) && !isLoadingFileLogs && availableFileLogs.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 rounded-xl border border-moss-primary/20 bg-moss-primary/5 p-3">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-moss-primary/15">
                      <span className="text-moss-primary text-xs">📄</span>
                    </div>
                    <div>
                      <p className="text-neutral-primary font-medium text-sm">
                        {availableFileLogs.length} documenten beschikbaar
                      </p>
                      <p className="text-neutral-secondary-mid text-xs">
                        Selecteer documenten die bij dit contract horen. Klik op 'Openen' om het document te bekijken.
                      </p>
                    </div>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto">
                    <div className="space-y-2">
                      {availableFileLogs.map((fileLog) => (
                        <div
                          key={fileLog.id}
                          className="flex items-center gap-3 p-3 rounded-xl border border-neutral-primary/10 bg-brick-secondary-fine/40"
                        >
                          <Checkbox
                            isSelected={selectedFileLogIds.includes(fileLog.id!)}
                            onValueChange={(checked) => handleFileLogSelection(fileLog.id!, checked)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="font-medium text-neutral-primary text-sm truncate">
                                {Array.isArray(fileLog.label) ? fileLog.label.join(', ') : fileLog.label}
                              </div>
                              <div className="px-2 py-1 bg-brick-primary/10 text-brick-primary rounded-sm text-xs whitespace-nowrap">
                                {formatDate(fileLog.createdAt)}
                              </div>
                            </div>
                            <div className="text-xs text-neutral-secondary-mid">
                              {fileLog.summary && (
                                <span className="mr-2">
                                  <strong>Samenvatting:</strong> {fileLog.summary}
                                </span>
                              )}
                              {fileLog.relations && fileLog.relations.length > 0 && (
                                <span className="mr-2">
                                  <strong>Relaties:</strong> {fileLog.relations.map(r => r.name).join(', ')}
                                </span>
                              )}
                              {fileLog.referenceCode && (
                                <span>
                                  <strong>Ref:</strong> {fileLog.referenceCode}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <ButtonNew
                              variant="secondaryNeutralMid"
                              onClick={() => handleOpenFileLog(fileLog)}
                              label="Openen"
                              isDisabled={!fileLog.sharePointFileUrl}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {selectedFileLogIds.length > 0 && (
                    <div className="text-center py-2 bg-moss-primary/10 rounded-lg border border-moss-primary/30">
                      <p className="text-xs text-moss-primary">
                        {selectedFileLogIds.length} document(en) geselecteerd om toe te voegen aan dit contract
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Odoo Sync Section */}
            <div className="space-y-4 rounded-2xl border border-neutral-primary/10 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-moss-primary/10 text-sm font-semibold text-moss-primary">7</span>
                <div>
                  <h3 className="text-base font-semibold text-neutral-primary">Synchronisatie met Odoo</h3>
                  <p className="text-xs text-neutral-secondary-mid">Boekhouding &amp; facturatie</p>
                </div>
              </div>
              
              <div className="rounded-xl border border-neutral-primary/10 bg-brick-secondary-fine/40 p-4">
                <Checkbox
                  isSelected={true}
                  isDisabled={true}
                >
                  Maak contract ook meteen aan in Odoo (verplicht)
                </Checkbox>
                <p className="text-xs text-neutral-secondary-mid mt-2">
                  Contract wordt automatisch aangemaakt in Odoo voor facturatie en administratie.
                </p>
              </div>
            </div>
              </>
            )}
          </div>
        </div>
        <div className="mt-3 border-t border-neutral-primary/10 bg-white pt-3">
          <div className="flex justify-between gap-2 w-full">
            <div>
              {wizardStep === "form" && (
                <ButtonNew
                  variant="secondaryNeutralMid"
                  onClick={() => setWizardStep("method")}
                  label="Terug"
                />
              )}
            </div>
            <div className="flex gap-2">
              <ButtonNew
                icon={<Close />}
                variant="secondaryNeutralMid"
                onClick={handleClose}
                label="Annuleren"
              />
              {wizardStep === "form" && (
                <ButtonNew
                  icon={<FloppyDisk />}
                  variant="primaryMoss"
                  onClick={handleCreateContract}
                  label="Contract opslaan"
                  isDisabled={isSaving}
                />
              )}
            </div>
          </div>
        </div>

      <AlertDialog
        open={equalPriceConfirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEqualPriceConfirmOpen(false);
            setEqualPriceProducts([]);
            setEqualPriceStartDateInfo({ startDate: null, daysSinceStart: null, yearsSinceStart: null, isSuspicious: false });
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Basishuurprijs gelijk aan geïndexeerde huurprijs
            </AlertDialogTitle>
            <AlertDialogDescription>
              Ben je zeker dat de basishuurprijs van dit product gelijk is aan
              de geïndexeerde huurprijs? Als de basishuurprijs verkeerd is
              ingegeven geeft dit mogelijk problemen bij latere indexaties!
            </AlertDialogDescription>
          </AlertDialogHeader>
          {equalPriceProducts.length > 0 && (
            <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
              <div className="mb-1 text-sm font-semibold text-neutral-primary">
                {equalPriceProducts.length === 1
                  ? "Volgend product:"
                  : "Volgende producten:"}
              </div>
              <ul className="list-inside list-disc text-sm text-neutral-secondary-mid">
                {equalPriceProducts.map((p, idx) => (
                  <li key={`${p.name}-${idx}`}>
                    <span className="font-medium text-neutral-primary">{p.name}</span>
                    {" — "}
                    Basishuurprijs €&nbsp;
                    {p.price.toFixed(2).replace(".", ",")} = Geïndexeerde prijs €&nbsp;
                    {p.price.toFixed(2).replace(".", ",")}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {equalPriceStartDateInfo.startDate && (() => {
            const startStr = equalPriceStartDateInfo.startDate.toLocaleDateString("nl-BE", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            });
            const years = equalPriceStartDateInfo.yearsSinceStart ?? 0;
            if (equalPriceStartDateInfo.isSuspicious) {
              const yearsLabel = years === 1 ? "1 jaar" : `${years} jaar`;
              return (
                <div className="flex items-start gap-3 rounded-md border-2 border-red-400 bg-red-50 p-3">
                  <Warning weight="fill" size={20} className="mt-0.5 shrink-0 text-red-600" />
                  <div className="text-sm text-red-800">
                    <div>
                      Startdatum: <span className="font-semibold">{startStr}</span> — {yearsLabel} geleden gestart.
                    </div>
                    <div className="mt-1">
                      Dit lijkt onmogelijk, tenzij er in het verleden indexaties zijn overgeslagen. Kijk dus zeker de basisprijs goed na!!!
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div className="flex items-start gap-3 rounded-md border border-green-300 bg-green-50 p-3">
                <div className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-green-500" aria-hidden />
                <div className="text-sm text-green-800">
                  <div>
                    Startdatum: <span className="font-semibold">{startStr}</span>.
                  </div>
                  <div className="mt-1">
                    Dit kan mogelijk zijn gezien het contract minder dan een jaar geleden is gestart.
                  </div>
                </div>
              </div>
            );
          })()}
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setEqualPriceConfirmOpen(false);
                setEqualPriceProducts([]);
            setEqualPriceStartDateInfo({ startDate: null, daysSinceStart: null, yearsSinceStart: null, isSuspicious: false });
              }}
            >
              Annuleer
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setEqualPriceConfirmed(true);
                setEqualPriceConfirmOpen(false);
                setEqualPriceProducts([]);
            setEqualPriceStartDateInfo({ startDate: null, daysSinceStart: null, yearsSinceStart: null, isSuspicious: false });
                // Re-trigger save now that the user has confirmed
                void handleCreateContract();
              }}
            >
              Toch aanmaken
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AddContractForm;
